import * as React from "react"
import { Col, Row } from "react-grid-system"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { theme } from "../theme"
import { useMyOrganization } from "../../hooks/useMyOrganization"
import { GroupModel, Member } from "../../utils/models"
import { ORGANIZATION_GROUPS_PATH } from "../../utils/paths"
import Card from "../atoms/Card"
import CardBar from "../atoms/CardBar"
import Body from "../atoms/typography/Body"
import H4 from "../atoms/typography/H4"
import SmallButton from "../atoms/buttons/SmallButton"
import TextButton from "../atoms/buttons/TextButton"
import { ArrowDropDownIcon } from "../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../atoms/icons/ArrowDropUpIcon"
import MemberProfileOverview from "../molecules/MemberProfileOverview"
import TerminalCardWithActions from "../molecules/TerminalCardWithActions"
import ViewReportButton from "../../reports/components/ViewReportButton"
import { groupReportOptions } from "../../reports/reports-helpers"

interface Props {
  group: GroupModel
}

const GroupOverviewCard: React.FunctionComponent<Props> = ({ group }) => {
  const { t } = useTranslation()
  const { myOrganization } = useMyOrganization()
  const [membersOpen, setMembersOpen] = React.useState(false)
  const [terminalsOpen, setTerminalsOpen] = React.useState(false)

  return (
    <div>
      <Card noPadding>
        <Card noPadding>
          <CardBar open>
            <Row justify="between" align="center">
              <div style={{ padding: "0 8px" }}>
                <Body color={theme.colors.white}>{myOrganization?.name}</Body>
                <H4 color={theme.colors.white}>{group.groupName}</H4>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 8px",
                }}
              >
                <ViewReportButton small options={groupReportOptions(parseInt(group.id))} />
                <Spacer spacing={1.5} />
                <Link to={ORGANIZATION_GROUPS_PATH}>
                  <SmallButton title="View in Organization" />
                </Link>
              </div>
            </Row>
          </CardBar>
          <HorizontalSpacer spacing={1} />
          <Row justify="center">
            <TextButton
              title={t("members")}
              icon={membersOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              onClick={() => setMembersOpen(!membersOpen)}
            />
          </Row>
          <HorizontalSpacer spacing={1} />
          {membersOpen && (
            <div>
              <Row style={{ padding: "0 15px" }}>
                {group.members.map((member: Member) => (
                  <Col md={3} key={member.member.id}>
                    <MemberProfileOverview
                      imgSrc={member.member.src ?? ""}
                      name={member.member.name}
                      role={member.member.role}
                    />
                  </Col>
                ))}
              </Row>
              <HorizontalSpacer spacing={1} />
            </div>
          )}
        </Card>
        <HorizontalSpacer spacing={1} />
        <Row justify="center">
          <TextButton
            title={t("terminals")}
            icon={terminalsOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            onClick={() => setTerminalsOpen(!terminalsOpen)}
          />
        </Row>
        <HorizontalSpacer spacing={1} />
        {terminalsOpen && (
          <div>
            <Row style={{ padding: "0 15px" }}>
              {group.terminals.map((terminal) => (
                <Col md={3} key={terminal.id}>
                  <TerminalCardWithActions
                    terminal={terminal}
                    groupId={group.id}
                    orgId={myOrganization?.id ?? "no org"}
                  />
                </Col>
              ))}
            </Row>
            <HorizontalSpacer spacing={1} />
          </div>
        )}
      </Card>
      <HorizontalSpacer spacing={3} />
    </div>
  )
}

export default GroupOverviewCard
